import { z } from 'zod';
import {
    IpxFlowFieldBindingSource,
    IpxManualCaptureTriggerKind,
    RpaBuilderCaptureElementKind,
    IpxWebResourceElementKind,
    validateIpxWebResourceModel,
} from '../samba-rpa-workflow.types';
import { IpxCredentialFieldNamePattern } from '../secret.types';
import type {
    GenericUxClickStep,
    GenericUxDoubleClickStep,
    GenericUxKeyModifier,
    GenericUxKeypressStep,
    GenericUxSequenceInit,
    GenericUxSequenceStep,
    GenericUxSetTextStep,
    GenericUxTypeTextStep,
    GenericUxWaitForSelectorStep,
    GenericUxWaitStep,
    IpxCredentialFlowFieldBinding,
    IpxDomElementOperationKind,
    IpxFlowFieldBindingSource as IpxFlowFieldBindingSourceType,
    IpxFlowElementAbsentOutcomePredicate,
    IpxFlowElementPresentOutcomePredicate,
    IpxFlowMappedTextContainsOutcomePredicate,
    IpxFlowMappedTextEqualsOutcomePredicate,
    IpxFlowOutcomePredicate,
    IpxFlowOutcomePredicateNormalizer,
    IpxFlowScreenReachedOutcomePredicate,
    IpxFlowUrlPatternPrerequisite,
    IpxFlowUrlPrerequisite,
    IpxLoginWorkflowStep,
    IpxManualCaptureTriggerKind as IpxManualCaptureTriggerKindType,
    IpxManualCaptureTriggerPolicy,
    RpaBuilderCaptureElementKind as RpaBuilderCaptureElementKindType,
    IpxWebResourceDomElementDescriptor,
    IpxWebResourceElementKind as IpxWebResourceElementKindType,
    IpxWebResourceElementEvidence,
    IpxWebResourceModelDocument,
    IpxWebResourceScreenMarkerKey,
    IpxWebResourceScreenMarkerMap,
    RpaCredentialFieldSelector,
    RpaCredentialFieldSelectorMap,
    RpaDomAttributeScreenDiscriminantExpression,
    RpaDomSelector,
    RpaDomSelectorFieldMap,
    RpaDomTextScreenDiscriminantExpression,
    RpaSanitizedDomSnapshot,
    RpaSanitizedDomStableAttributeMap,
    RpaScreenDefinition,
    RpaScreenDetectionPredicate,
    RpaScreenDetectionProfile,
    RpaScreenDiscriminantExpression,
    RpaScreenKey,
} from '../samba-rpa-workflow.types';
import { UrlPatternStringSchema } from '../backend_config.types';
import { NonEmptyStringSchema, PositiveIntSchema } from './common.schemas';

export const GenericUxFramePathSchema: z.ZodType<number[]> = z.array(
    z.number().int().min(0)
);

export const GenericUxTimeoutMsSchema: z.ZodType<number> = PositiveIntSchema;

export const GenericUxDelayMsSchema: z.ZodType<number> = z
    .number()
    .int()
    .min(0);

export const RpaDomSelectorSchema: z.ZodType<RpaDomSelector> = z.object({
    selector: NonEmptyStringSchema,
    innerText: z.string().optional(),
    textContent: z.string().optional(),
    framePath: GenericUxFramePathSchema.optional(),
});

export const RpaDomSelectorFieldMapSchema: z.ZodType<RpaDomSelectorFieldMap> =
    z.record(NonEmptyStringSchema, RpaDomSelectorSchema);

export const RpaScreenKeySchema: z.ZodType<RpaScreenKey> = NonEmptyStringSchema;

export const RpaDomTextScreenDiscriminantExpressionSchema: z.ZodType<
    RpaDomTextScreenDiscriminantExpression
> = z.object({
    kind: z.literal('dom_text'),
    selector: RpaDomSelectorSchema,
    normalizer: z.literal('visible_text_v1'),
});

export const RpaDomAttributeScreenDiscriminantExpressionSchema: z.ZodType<
    RpaDomAttributeScreenDiscriminantExpression
> = z.object({
    kind: z.literal('dom_attribute'),
    selector: RpaDomSelectorSchema,
    attribute: NonEmptyStringSchema,
    normalizer: z.literal('trim_v1'),
});

export const RpaScreenDiscriminantExpressionSchema: z.ZodType<
    RpaScreenDiscriminantExpression
> = z.union([
    RpaDomTextScreenDiscriminantExpressionSchema,
    RpaDomAttributeScreenDiscriminantExpressionSchema,
]);

export const RpaScreenDetectionPredicateSchema: z.ZodType<
    RpaScreenDetectionPredicate
> = z.object({
    expression: RpaScreenDiscriminantExpressionSchema,
    expected_value: NonEmptyStringSchema,
});

export const RpaScreenDefinitionSchema: z.ZodType<RpaScreenDefinition> =
    z.object({
        screen_key: RpaScreenKeySchema,
        display_name: NonEmptyStringSchema,
        detection_predicate: RpaScreenDetectionPredicateSchema.optional(),
    });

export const RpaScreenDetectionProfileSchema: z.ZodType<
    RpaScreenDetectionProfile
> = z
    .object({
        screens: z.record(RpaScreenKeySchema, RpaScreenDefinitionSchema),
    })
    .superRefine((profile, ctx) => {
        for (const screenKey of Object.keys(profile.screens)) {
            const definition = profile.screens[screenKey];
            if (definition === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Screen definition is missing',
                    path: ['screens', screenKey],
                });
                continue;
            }

            if (definition.screen_key !== screenKey) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Screen definition key must match map key',
                    path: ['screens', screenKey, 'screen_key'],
                });
            }
        }
    });

export const RpaScreenDiscriminantProfileSchema: z.ZodType<
    RpaScreenDetectionProfile
> = RpaScreenDetectionProfileSchema;

export const RpaSanitizedDomStableAttributeMapSchema: z.ZodType<
    RpaSanitizedDomStableAttributeMap
> = z.record(NonEmptyStringSchema, z.string());

export const RpaSanitizedDomSnapshotSchema: z.ZodType<RpaSanitizedDomSnapshot> =
    z.lazy(() =>
        z.object({
            tag_name: NonEmptyStringSchema,
            stable_attributes: RpaSanitizedDomStableAttributeMapSchema,
            visible_text: z.string().optional(),
            children: z.array(RpaSanitizedDomSnapshotSchema),
        })
    );

export const RpaCredentialFieldSelectorSchema: z.ZodType<
    RpaCredentialFieldSelector
> = z.object({
    selector: NonEmptyStringSchema,
    innerText: z.string().optional(),
    required_screen_key: RpaScreenKeySchema.optional(),
});

export const RpaCredentialFieldSelectorMapSchema: z.ZodType<
    RpaCredentialFieldSelectorMap
> = z.record(NonEmptyStringSchema, RpaCredentialFieldSelectorSchema);

export const IpxDomElementOperationKindSchema: z.ZodType<IpxDomElementOperationKind> =
    z.union([
        z.literal('read'),
        z.literal('click'),
        z.literal('double_click'),
        z.literal('paste_text'),
        z.literal('type_text'),
    ]);

export const IpxWebResourceElementKindSchema: z.ZodType<IpxWebResourceElementKindType> =
    z.enum(IpxWebResourceElementKind);

export const IpxWebResourceElementEvidenceSchema: z.ZodType<IpxWebResourceElementEvidence> =
    z
        .object({
            stable_text: NonEmptyStringSchema.optional(),
            stable_attributes:
                RpaSanitizedDomStableAttributeMapSchema.optional(),
        })
        .strict()
        .superRefine((evidence, ctx) => {
            if (
                evidence.stable_text === undefined &&
                evidence.stable_attributes === undefined
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message:
                        'Mapped element evidence must contain stable text or stable attributes',
                });
            }
        });

export const IpxWebResourceScreenMarkerKeySchema: z.ZodType<IpxWebResourceScreenMarkerKey> =
    NonEmptyStringSchema;

export const IpxWebResourceScreenMarkerMapSchema: z.ZodType<IpxWebResourceScreenMarkerMap> =
    z.record(IpxWebResourceScreenMarkerKeySchema, RpaDomSelectorSchema);

export const IpxFlowFieldBindingSourceSchema: z.ZodType<IpxFlowFieldBindingSourceType> =
    z.enum(IpxFlowFieldBindingSource);

export const IpxCredentialFlowFieldNameSchema: z.ZodType<string> = z
    .string()
    .min(1)
    .max(64)
    .regex(IpxCredentialFieldNamePattern);

export const IpxCredentialFlowFieldBindingSchema: z.ZodType<IpxCredentialFlowFieldBinding> =
    z
        .object({
            credential_field_name: IpxCredentialFlowFieldNameSchema,
            source: IpxFlowFieldBindingSourceSchema,
            element_key: NonEmptyStringSchema,
        })
        .strict();

export const RpaBuilderCaptureElementKindSchema: z.ZodType<RpaBuilderCaptureElementKindType> =
    z.enum(RpaBuilderCaptureElementKind);

export const IpxLoginWorkflowStepSchema: z.ZodType<IpxLoginWorkflowStep> =
    z
        .object({
            kind: RpaBuilderCaptureElementKindSchema,
            element_key: NonEmptyStringSchema,
        })
        .strict();

export const IpxLoginWorkflowStepListSchema: z.ZodType<
    readonly IpxLoginWorkflowStep[]
> = z.array(IpxLoginWorkflowStepSchema);

export const IpxFlowOutcomePredicateNormalizerSchema: z.ZodType<IpxFlowOutcomePredicateNormalizer> =
    z.literal('trim_v1');

export const IpxFlowScreenReachedOutcomePredicateSchema: z.ZodType<IpxFlowScreenReachedOutcomePredicate> =
    z
        .object({
            kind: z.literal('marker_context_reached'),
            required_screen_marker_keys: z
                .array(IpxWebResourceScreenMarkerKeySchema)
                .min(1),
        })
        .strict();

export const IpxFlowMappedTextEqualsOutcomePredicateSchema: z.ZodType<IpxFlowMappedTextEqualsOutcomePredicate> =
    z
        .object({
            kind: z.literal('mapped_text_equals'),
            element_key: NonEmptyStringSchema,
            expected_value: NonEmptyStringSchema,
            normalizer: IpxFlowOutcomePredicateNormalizerSchema,
        })
        .strict();

export const IpxFlowMappedTextContainsOutcomePredicateSchema: z.ZodType<IpxFlowMappedTextContainsOutcomePredicate> =
    z
        .object({
            kind: z.literal('mapped_text_contains'),
            element_key: NonEmptyStringSchema,
            expected_value: NonEmptyStringSchema,
            normalizer: IpxFlowOutcomePredicateNormalizerSchema,
        })
        .strict();

export const IpxFlowElementPresentOutcomePredicateSchema: z.ZodType<IpxFlowElementPresentOutcomePredicate> =
    z
        .object({
            kind: z.literal('element_present'),
            element_key: NonEmptyStringSchema,
        })
        .strict();

export const IpxFlowElementAbsentOutcomePredicateSchema: z.ZodType<IpxFlowElementAbsentOutcomePredicate> =
    z
        .object({
            kind: z.literal('element_absent'),
            element_key: NonEmptyStringSchema,
        })
        .strict();

export const IpxFlowOutcomePredicateSchema: z.ZodType<IpxFlowOutcomePredicate> =
    z.union([
        IpxFlowScreenReachedOutcomePredicateSchema,
        IpxFlowMappedTextEqualsOutcomePredicateSchema,
        IpxFlowMappedTextContainsOutcomePredicateSchema,
        IpxFlowElementPresentOutcomePredicateSchema,
        IpxFlowElementAbsentOutcomePredicateSchema,
    ]);

export const IpxFlowUrlPatternPrerequisiteSchema: z.ZodType<IpxFlowUrlPatternPrerequisite> =
    z
        .object({
            kind: z.literal('url_pattern'),
            url_pattern: UrlPatternStringSchema,
        })
        .strict();

export const IpxFlowUrlPrerequisiteSchema: z.ZodType<IpxFlowUrlPrerequisite> =
    IpxFlowUrlPatternPrerequisiteSchema;

export const IpxManualCaptureTriggerKindSchema: z.ZodType<IpxManualCaptureTriggerKindType> =
    z.enum(IpxManualCaptureTriggerKind);

export const IpxManualCaptureTriggerPolicySchema: z.ZodType<IpxManualCaptureTriggerPolicy> =
    z
        .object({
            trigger_kinds: z.array(IpxManualCaptureTriggerKindSchema).min(1),
        })
        .strict()
        .superRefine((policy, ctx) => {
            const seenTriggerKinds = new Set<IpxManualCaptureTriggerKindType>();
            for (const [triggerIndex, triggerKind] of policy.trigger_kinds.entries()) {
                if (seenTriggerKinds.has(triggerKind)) {
                    ctx.addIssue({
                        code: 'custom',
                        message:
                            'manual capture trigger kinds must be unique',
                        path: ['trigger_kinds', triggerIndex],
                    });
                }
                seenTriggerKinds.add(triggerKind);
            }
        });

export const IpxWebResourceDomElementDescriptorSchema: z.ZodType<IpxWebResourceDomElementDescriptor> =
    z
        .object({
            element_key: NonEmptyStringSchema,
            display_name: NonEmptyStringSchema,
            selector: RpaDomSelectorSchema,
            element_kind: IpxWebResourceElementKindSchema,
            required_screen_marker_keys: z
                .array(IpxWebResourceScreenMarkerKeySchema)
                .min(1),
            evidence: IpxWebResourceElementEvidenceSchema.optional(),
            notes: NonEmptyStringSchema.optional(),
        })
        .strict()
        .superRefine((element, ctx) => {
            const seenMarkerKeys = new Set<string>();
            for (const [
                markerIndex,
                markerKey,
            ] of element.required_screen_marker_keys.entries()) {
                if (seenMarkerKeys.has(markerKey)) {
                    ctx.addIssue({
                        code: 'custom',
                        message:
                            'DOM element screen marker keys must be unique',
                        path: ['required_screen_marker_keys', markerIndex],
                    });
                }
                seenMarkerKeys.add(markerKey);
            }
        });

export const IpxWebResourceModelDocumentSchema: z.ZodType<IpxWebResourceModelDocument> =
    z
        .object({
            screen_markers: IpxWebResourceScreenMarkerMapSchema,
            dom_elements: z.array(IpxWebResourceDomElementDescriptorSchema),
        })
        .strip()
        .superRefine((document, ctx) => {
            for (const issue of validateIpxWebResourceModel(document).issues) {
                ctx.addIssue({
                    code: 'custom',
                    message: issue.message,
                    path: [...issue.path],
                });
            }
        });

export const GenericUxKeyModifierSchema: z.ZodType<GenericUxKeyModifier> =
    z.union([
        z.literal('Alt'),
        z.literal('Control'),
        z.literal('Meta'),
        z.literal('Shift'),
    ]);

export const GenericUxClickStepSchema: z.ZodType<GenericUxClickStep> =
    z.object({
        action: z.literal('click'),
        selector: NonEmptyStringSchema,
        framePath: GenericUxFramePathSchema.optional(),
        timeoutMs: GenericUxTimeoutMsSchema.optional(),
    });

export const GenericUxDoubleClickStepSchema: z.ZodType<GenericUxDoubleClickStep> =
    z.object({
        action: z.literal('doubleClick'),
        selector: NonEmptyStringSchema,
        framePath: GenericUxFramePathSchema.optional(),
        timeoutMs: GenericUxTimeoutMsSchema.optional(),
    });

export const GenericUxSetTextStepSchema: z.ZodType<GenericUxSetTextStep> =
    z.object({
        action: z.literal('setText'),
        selector: NonEmptyStringSchema,
        text: z.string(),
        framePath: GenericUxFramePathSchema.optional(),
        timeoutMs: GenericUxTimeoutMsSchema.optional(),
    });

export const GenericUxTypeTextStepSchema: z.ZodType<GenericUxTypeTextStep> =
    z.object({
        action: z.literal('typeText'),
        selector: NonEmptyStringSchema,
        text: z.string(),
        framePath: GenericUxFramePathSchema.optional(),
        timeoutMs: GenericUxTimeoutMsSchema.optional(),
        delayMs: GenericUxDelayMsSchema.optional(),
    });

export const GenericUxKeypressStepSchema: z.ZodType<GenericUxKeypressStep> =
    z.object({
        action: z.literal('keypress'),
        key: NonEmptyStringSchema,
        modifiers: z.array(GenericUxKeyModifierSchema).optional(),
        selector: NonEmptyStringSchema.optional(),
        framePath: GenericUxFramePathSchema.optional(),
    });

export const GenericUxWaitStepSchema: z.ZodType<GenericUxWaitStep> =
    z.object({
        action: z.literal('wait'),
        milliseconds: PositiveIntSchema,
    });

export const GenericUxWaitForSelectorStepSchema: z.ZodType<GenericUxWaitForSelectorStep> =
    z.object({
        action: z.literal('waitForSelector'),
        selector: NonEmptyStringSchema,
        framePath: GenericUxFramePathSchema.optional(),
        timeoutMs: GenericUxTimeoutMsSchema.optional(),
    });

export const GenericUxSequenceStepSchema: z.ZodType<GenericUxSequenceStep> =
    z.union([
        GenericUxClickStepSchema,
        GenericUxDoubleClickStepSchema,
        GenericUxSetTextStepSchema,
        GenericUxTypeTextStepSchema,
        GenericUxKeypressStepSchema,
        GenericUxWaitStepSchema,
        GenericUxWaitForSelectorStepSchema,
    ]);

export const GenericUxSequenceInitSchema: z.ZodType<GenericUxSequenceInit> =
    z.object({
        tabId: PositiveIntSchema,
        steps: z.array(GenericUxSequenceStepSchema).min(1),
    });
